import { translate } from "../../../../utils/Common";
import { cn } from "@/lib/utils";

/**
 * Packages per locker, as a horizontal "top list".
 *
 * It was a pie, then a Recharts bar chart with the locker name on the Y axis.
 * Both lost to the same problem: locker names are long ("TEST-LINUX-WAREHOUSE-CLERMONT")
 * and a Y axis gives them a fixed sliver of width, so the name wrapped onto two
 * lines inside a one-line row and then got clipped — from the *front*, which is
 * exactly the part that identifies the locker.
 *
 * So the name moves above its bar and gets the full width of the card, with the
 * value right-aligned on the same line (a column of digits reads top-to-bottom).
 * No axis, no chart library: a bar whose length is a percentage is a div, and the
 * whole class of wrap/clip bug goes away with the SVG.
 *
 * One hue for the whole series, because the job is magnitude, not identity:
 * length carries the comparison and the sort order already says who leads.
 * Colour would follow rank, not the locker — it would repaint when the data moves.
 */
const PlatformBars = ({ data, max = 5, emptyLabel, valueLabel, className }) => {
  // null means the fetch has not answered yet; [] means it answered with
  // nothing. Collapsing the two paints "no packages" for a moment and then
  // swaps in the bars, which reads as the screen changing its mind.
  if (data == null) {
    return (
      <div className={cn('space-y-2 py-2', className)} aria-hidden="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="bx-skeleton h-3 w-28 rounded" />
            <div className="bx-skeleton h-3 flex-1 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const all = [...data].filter((d) => (d?.value ?? 0) > 0).sort((a, b) => b.value - a.value);
  const rows = all.slice(0, max);

  // Never drop rows in silence: what the top N leaves out is shown as one
  // aggregate, so the bars still add up to the total in the card header.
  const restCount = all.length - rows.length;
  const restValue = all.slice(max).reduce((n, d) => n + d.value, 0);

  if (!rows.length) {
    return (
      <p className="m-0 py-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>
    );
  }

  const top = rows[0].value;
  // A locker with 2 of 248 packages still has to be visible as a bar.
  const width = (value) => `${Math.max((value / top) * 100, 1.5)}%`;

  const Row = ({ name, value, muted }) => (
    <li>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={cn(
            "min-w-0 truncate text-[0.8125rem] font-medium",
            muted ? "text-muted-foreground" : "text-foreground"
          )}
          title={valueLabel ? `${name} — ${value} ${valueLabel}` : name}
        >
          {name}
        </span>
        <span className="shrink-0 text-[0.8125rem] font-semibold tabular-nums text-foreground">
          {value}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className={cn("h-full rounded-full", muted ? "bg-muted-foreground/40" : "bg-primary")}
          style={{ width: width(value), minWidth: 3 }}
        />
      </div>
    </li>
  );

  return (
    <ul className={cn("m-0 flex min-h-[6.5rem] list-none flex-col gap-3 p-0", className)}>
      {rows.map((row) => (
        <Row key={row.name} name={row.name} value={row.value} />
      ))}
      {restCount > 0 && (
        <Row name={`${translate("dashboard.home.platform_bars.others")} (${restCount})`} value={restValue} muted />
      )}
    </ul>
  );
};

export default PlatformBars;
